import { sha256 } from 'js-sha256';

import { CurrencyCode } from '@/__generated__/storefront/graphql';
import { posthogEvents } from '@/shared/constants/constants';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

type FeatureName =
  'pattern_product_suggestions' | 'product_review' | 'yarn_patterns';

export type UserEventDefinitions = {
  'show popup': {
    category: string;
    label: string;
  };
  'close popup': {
    category: string;
    label: string;
  };
  'clicked skip form cta': {
    category: string;
    label: string;
  };
  'clicked on signup': {
    category: string;
    label: string;
  };
  pattern_download_signup: {
    category: string;
    customer_name_hash?: string;
    customer_email_hash?: string;
    currency: CurrencyCode;
  };
  newsletter_signup: {
    category: string;
    campaign_name?: string;
    customer_name_hash?: string;
    customer_email_hash?: string;
  };
  pattern_products_add_to_cart: {
    category: string;
    label?: string;
    currency: CurrencyCode;
    items: {
      item_id: string;
      item_name?: string;
      item_category?: string;
      item_variant?: string;
      price: number;
      quantity?: number;
    }[];
    value: number;
  };
  pattern_products_add_all_to_cart: {
    category: 'all_recommendations';
    label?: string;
    currency: CurrencyCode;
    items: {
      item_id: string;
      item_name?: string;
      item_category?: string;
      item_variant?: string;
      price: number;
      quantity?: number;
    }[];
    value: number;
  };
  feature_view: {
    category: 'section' | 'modal';
    item_name: FeatureName;
    label: string;
  };
  add_review: {
    category: 'Product Review';
    item_id: string;
    item_name: string;
    label: string;
    value: number;
  };
  show_original_review: {
    category: 'Product Review';
    item_id: string;
    item_name: string;
    label: string;
    value: number;
  };
  page_view: {
    page_title: string;
  };
  collection_metadata: {
    collection_hierarchy?: string;
  };
};

type EventNames = keyof UserEventDefinitions;

type TrackHobbiiEvent = <T extends EventNames>(
  event: T,
  data: UserEventDefinitions[T]
) => void;

export type DownloadPatternData = {
  action: 'download_pattern';
  category: 'plus_pattern' | 'free_pattern';
  label: string;
  item_id: string | number;
  sku: string;
  market: string;
  currency: CurrencyCode;
  variant_id: number | null;
  product_id: number | null;
};

// only event that is tracked with another event name than hobbii_event
export const trackDownloadPattern = (data: DownloadPatternData) => {
  // For Google Analytics/GTM - send only original fields to maintain backward compatibility
  window.dataLayer?.push({
    event: 'download_pattern',
    user_event_data: {
      action: data.action,
      category: data.category,
      label: data.label,
      item_id: data.item_id
    }
  });

  // For PostHog - use structure with all new PostHog specific fields
  window.posthog?.capture(posthogEvents.PATTERN_DOWNLOAD, {
    pattern_sku: data.sku,
    pattern_name: data.label,
    pattern_type: data.category,
    market: data.market,
    currency: data.currency
  });

  // For Klaviyo - onsite event for flow triggers (CA-72)
  void window.klaviyo?.track('Downloaded Pattern', {
    pattern_variant_id: data.variant_id,
    pattern_product_id: data.product_id,
    pattern_name: data.label
  });
};

export type KlaviyoIdentifyData = {
  email: string;
  firstName?: string;
};

export const klaviyoIdentify = ({ email, firstName }: KlaviyoIdentifyData) => {
  if (!email) return;
  void window.klaviyo?.identify({
    email,
    ...(firstName ? { first_name: firstName } : {})
  });
};

export const trackHobbiiEvent: TrackHobbiiEvent = (eventName, data) => {
  try {
    window.Shopify.analytics.publish('hobbii_event', {
      // We need to use hobbii_event for all new events except download_pattern, to avoid configuring GTM for every new type of event
      eventName,
      data
    });
  } catch (error) {
    notifyBugsnagWithContext('trackHobbiiEvent', error, {
      data: {
        eventName,
        data
      }
    });
  }
};

// We use the algorithm sha256 for encoding according to Meta Pixel documentation
// https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
// https://developers.google.com/google-ads/api/docs/conversions/enhanced-conversions/web#prepare-data
export const hashUserData = (value: string) => {
  return sha256(value.trim().toLowerCase());
};
